import { useState } from "react";
import { BookMarked, ExternalLink, Flag, Check } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { TOPIC_PROVENANCE, ERRATA_EMAIL } from "@/data/topicSources";

interface Props {
  topicId: string;
  topicTitle: string;
  topicNumber: number;
}

/**
 * Per-topic Sources & "last reviewed" footer with a built-in errata reporter.
 * The reporter opens the user's mail client with a pre-filled subject + body
 * - no data is sent from ElementPad itself, keeping the privacy-first contract.
 */
export default function TopicSourcesFooter({ topicId, topicTitle, topicNumber }: Props) {
  const provenance = TOPIC_PROVENANCE[topicId];
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [issue, setIssue] = useState("");
  const [sent, setSent] = useState(false);

  if (!provenance) return null;

  const reviewedDate = new Date(provenance.lastReviewed + "T00:00:00");
  const formatted = reviewedDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const handleSend = () => {
    const subject = encodeURIComponent(
      `[Errata] Topic ${String(topicNumber).padStart(2, "0")} - ${topicTitle}`
    );
    const body = encodeURIComponent(
      `Topic: ${topicTitle} (id: ${topicId})\n` +
        `Last reviewed: ${provenance.lastReviewed}\n\n` +
        `Issue:\n${issue || "(describe the typo, bad question, or factual error)"}\n\n` +
        `Suggested fix:\n\n\n` +
        `Reporter (optional name / role):\n`
    );
    window.location.href = `mailto:${ERRATA_EMAIL}?subject=${subject}&body=${body}`;
    setSent(true);
    setTimeout(() => {
      setReportOpen(false);
      setSent(false);
      setIssue("");
    }, 1500);
  };

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-left group"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <BookMarked size={14} className="text-primary" aria-hidden="true" />
          <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
            Sources & accuracy
          </span>
          <span className="text-[10px] font-mono text-muted-foreground">
            Reviewed {formatted}
          </span>
        </div>
        <span className="text-[10px] text-muted-foreground group-hover:text-foreground transition-colors">
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <ul className="space-y-1.5">
            {provenance.sources.map((s) => (
              <li
                key={s.label}
                className="text-xs leading-snug flex items-start gap-1.5"
              >
                <span className="text-muted-foreground mt-0.5">•</span>
                <span className="flex-1">
                  {s.url ? (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground hover:text-primary underline decoration-dotted underline-offset-2 inline-flex items-center gap-1"
                    >
                      {s.label}
                      <ExternalLink size={10} aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="text-foreground">{s.label}</span>
                  )}
                  {s.note && (
                    <span className="block text-muted-foreground mt-0.5">
                      {s.note}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
            <p className="text-[11px] text-muted-foreground">
              Spot a typo, a bad question, or a factual error?
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              onClick={() => setReportOpen((v) => !v)}
              aria-expanded={reportOpen}
            >
              <Flag size={12} aria-hidden="true" />
              Report errata
            </Button>
          </div>

          {reportOpen && (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 space-y-2">
              <label
                htmlFor={`errata-${topicId}`}
                className="text-[10px] font-semibold uppercase tracking-wide text-foreground"
              >
                Describe the issue
              </label>
              <textarea
                id={`errata-${topicId}`}
                value={issue}
                onChange={(e) => setIssue(e.target.value)}
                rows={3}
                placeholder="e.g. Q2 distractor is wrong - N has higher IE than O is the misconception, not the answer."
                className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] text-muted-foreground">
                  Opens your mail app - nothing is sent through ElementPad.
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setReportOpen(false);
                      setIssue("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={handleSend}
                    disabled={sent}
                  >
                    {sent ? (
                      <>
                        <Check size={12} aria-hidden="true" /> Opened
                      </>
                    ) : (
                      <>
                        <Flag size={12} aria-hidden="true" /> Send report
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}